/*
 * /cart page controller — replaces "mutate the cart, then window.location.reload()".
 *
 * - One live cart state (snippets/cart-page-state.liquid): parsed from the page on load,
 *   then re-read from the Section Rendering API payload every /cart/*.js mutation returns.
 * - Quantity / remove are optimistic: the row and the order-summary numbers change on tap,
 *   the request goes out debounced and strictly in order, and a failure rolls back only
 *   that row with an inline message.
 * - Once the queue is idle, one ordered refresh re-runs what the page used to recompute on
 *   reload (rewards cap, fees, coupon / handle-cart, gifts, EDD, review step) in place.
 *
 * Other cart code (coupons, BXGY, hidden lines) mutates through CartPage.change / add /
 * update, and wraps multi-step changes in CartPage.batch so the refresh runs once at the end.
 *
 * The page renders both the full cart and the empty state; this toggles between them when the
 * cart empties or gains its first item. Code that only makes sense with items (address list,
 * coupons, EDD) registers through CartPage.onItems, the empty-cart add-ons through onEmpty.
 *
 * Loaded synchronously by cart-revamp.liquid, right after the state snippet, so every
 * inline cart script can read window.CartPage while the page parses.
 */
(function () {
  'use strict';

  var STATE_SECTION = 'cart-page-state';
  var ROWS_SECTION = 'cart-page-rows';
  var QTY_DEBOUNCE_MS = 300;
  var ROW_ERROR_MS = 4000;

  /* ---- Timing (opt-in: localStorage.cartPageDebug = '1') --------------------- */

  // For QA against the PRD budget: tap → on-screen update, tap → settled totals, and bytes per
  // cart write, logged to the console. Off unless the flag is set.
  var debug = false;
  try { debug = localStorage.getItem('cartPageDebug') === '1'; } catch (e) {}
  var tapAt = 0;
  function markTap() { if (debug) tapAt = performance.now(); }
  function logTiming(label, extra) {
    if (!debug) return;
    var ms = tapAt ? Math.round(performance.now() - tapAt) : null;
    console.info('[CartPage] ' + label + (ms !== null ? ' ' + ms + 'ms after tap' : ''), extra || '');
  }

  /* ---- Duplicate-request guards ----------------------------------------------- */

  // The gift checks (IPA, surprise gift) are asked for by several paths at once — the cart refresh,
  // #changeeddbtn clicks, address events. `dedupe` runs `run` once per distinct `key` and hands the
  // same promise to every caller with that key, so an unchanged cart/location costs nothing.
  var dedupeCache = {};
  function dedupe(name, key, run) {
    var entry = dedupeCache[name];
    if (entry && entry.key === key) return entry.promise;
    var promise = Promise.resolve().then(run);
    dedupeCache[name] = { key: key, promise: promise };
    promise.catch(function () {
      if (dedupeCache[name] && dedupeCache[name].promise === promise) delete dedupeCache[name];
    });
    return promise;
  }
  function forget(name) { delete dedupeCache[name]; }

  // Saved-address reads are fired by several listeners for the same address on every step change
  // (open/close review, Proceed). Identical GETs share one response for a few seconds; any address
  // write, or a saved-address event, drops the cache.
  var ADDRESS_READ = /\/customer\/(address\/web\/[^/?]+\/[^/?]+|address-list\/web\/[^/?]+)(\?|$)/;
  var ADDRESS_TTL_MS = 10000;
  var addressReads = {};
  function clearAddressReads() { addressReads = {}; }

  function installAddressReadCache() {
    var nativeFetch = window.fetch;
    if (!nativeFetch || nativeFetch.__cartPageAddressCache) return;
    var cachedFetch = function (input, init) {
      var url = typeof input === 'string' ? input : (input && input.url) || '';
      var method = ((init && init.method) || (input && input.method) || 'GET').toUpperCase();
      if (/\/customer\/address/.test(url) && method !== 'GET') clearAddressReads();
      if (method !== 'GET' || !ADDRESS_READ.test(url)) return nativeFetch.apply(this, arguments);
      var hit = addressReads[url];
      if (!hit || Date.now() - hit.at > ADDRESS_TTL_MS) {
        hit = { at: Date.now(), response: nativeFetch.call(window, input, init) };
        addressReads[url] = hit;
        hit.response.then(function (r) { if (!r.ok) delete addressReads[url]; }, function () { delete addressReads[url]; });
      }
      return hit.response.then(function (r) { return r.clone(); });
    };
    cachedFetch.__cartPageAddressCache = true;
    window.fetch = cachedFetch;
    window.addEventListener('addressSavedSuccessfully', clearAddressReads);
  }

  /* ---- State ---------------------------------------------------------------- */

  function readState(root) {
    var el = root && root.querySelector('[data-cart-page-state]');
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      console.error('[CartPage] could not parse cart state', e);
      return null;
    }
  }

  function stateFromHtml(html) {
    if (!html) return null;
    var tpl = document.createElement('template');
    tpl.innerHTML = html;
    return readState(tpl.content);
  }

  var initialState = readState(document);
  var state = initialState || { cart: { items: [], item_count: 0 }, lines: {}, totals: {} };
  var mutated = false;         // the cart changed (or a refresh was asked for) since the last refresh started
  var changedSinceLoad = false;

  function setState(next) {
    if (!next || !next.cart) return;
    // A write that left the cart as it was (e.g. a rejected change re-reading state) needs no refresh.
    if (JSON.stringify(next.cart) !== JSON.stringify(state.cart)) {
      mutated = true;
      changedSinceLoad = true;
    }
    state = next;
    syncGlobals();
  }

  // Keep the page's own copies of the cart in step; each of these was a Liquid-baked
  // snapshot that only a reload used to refresh.
  function syncGlobals() {
    window.shopifyCart = state.cart;
    /* eslint-disable no-undef */
    if (typeof cartTotalSavings !== 'undefined') cartTotalSavings = Math.floor(totals().savings / 100);
    if (typeof prodDetails !== 'undefined') prodDetails = state.cart.items;
    if (typeof pharmaItems !== 'undefined') pharmaItems = medicineItems();
    /* eslint-enable no-undef */
  }

  function cart() { return state.cart; }
  function items() { return state.cart.items || []; }
  function totals() { return state.totals || {}; }
  function meta(key) { return (state.lines && state.lines[key]) || {}; }
  function findItem(key) {
    var list = items();
    for (var i = 0; i < list.length; i++) if (list[i].key === key) return list[i];
    return null;
  }
  function tagsOf(item) { return meta(item.key).tags || []; }
  // Liquid's `contains` on the joined tag string is a substring match, on the tag array an
  // exact match; the helpers below mirror whichever form the original Liquid used.
  function tagString(item) { return tagsOf(item).join(','); }
  function hasTag(item, tag) { return tagsOf(item).indexOf(tag) > -1; }

  /* ---- Derived data (replaces the Liquid-baked copies) ---------------------- */

  // updateTags(): [{ "<variant id>": "tag1,tag2" }]
  function tagsById() {
    return items().map(function (item) {
      var o = {};
      o[item.id] = tagString(item);
      return o;
    });
  }

  // updateTags2(): [{ tags: "tag1,tag2" }]
  function tagsList() {
    return items().map(function (item) { return { tags: tagString(item) }; });
  }

  function collectionHandles(item) { return meta(item.key).collections || []; }

  function allCollectionHandles() {
    var out = [];
    items().forEach(function (item) { out = out.concat(collectionHandles(item)); });
    return out;
  }

  // { sku: true } for surprise / free-gift lines — EDD skips these.
  function tagsToIdFinal() {
    var map = {};
    items().forEach(function (item) {
      var t = tagString(item);
      map[item.sku] = t.indexOf('surprise_gift') > -1 || t.indexOf('free_gift') > -1;
    });
    return map;
  }

  function surpriseGiftVariantIds() {
    return items().filter(function (item) { return hasTag(item, 'surprise_gift'); })
      .map(function (item) { return item.variant_id; });
  }

  function medicineItems() {
    var out = [];
    items().forEach(function (item) {
      if (!hasTag(item, 'Veterinary Diet')) return;
      out.push({
        id: String(item.variant_id),
        qty: String(item.quantity),
        prescription: (item.properties && item.properties.prescription_file) || ''
      });
    });
    return out;
  }

  // Line items for the backend's /cart/handle-cart.
  function handleCartLineItems() {
    return items().map(function (item) {
      return { variantId: String(item.variant_id), quantity: item.quantity };
    });
  }

  // Identifies the lines a handle-cart call was made for.
  function handleCartKey() {
    return JSON.stringify(handleCartLineItems());
  }

  /* ---- handle-cart (the backend's order data, read at order placement) ------- */

  // Every handle-cart call goes through here. Calls run one at a time, in order, so an older
  // response can never land after a newer one; a response for lines that changed while it was
  // in flight is not stored (the refresh that follows the change asks again). What is stored
  // is stamped with the lines + coupon it was made for, so order placement can tell whether
  // localStorage["handle-cart"] still matches the cart (ensureHandleCart).
  var HANDLE_CART_URL = 'https://supertails-backend.el.r.appspot.com/cart/handle-cart';
  var HANDLE_CART_STAMP = 'handle-cart-stamp';
  var handleCartChain = Promise.resolve();
  var handleCartQueued = null;  // the call waiting its turn: { coupon, promise }

  function handleCartStamp(coupon) {
    return handleCartKey() + '|' + (coupon || '');
  }

  function handleCartFresh(coupon) {
    try {
      return !!localStorage.getItem('handle-cart') && localStorage.getItem(HANDLE_CART_STAMP) === handleCartStamp(coupon);
    } catch (e) {
      return false;
    }
  }

  // Resolves with the backend's response (result.stale = true when it wasn't stored).
  function requestHandleCart(coupon, email) {
    // discountCode is stored trimmed; trim here too so the stamp matches it at order time.
    coupon = String(coupon || '').trim();
    // A queued call for the same coupon would send the same request: share it.
    if (handleCartQueued && handleCartQueued.coupon === coupon) return handleCartQueued.promise;
    var entry = { coupon: coupon };
    entry.promise = handleCartChain.then(function () {
      if (handleCartQueued === entry) handleCartQueued = null;
      var stamp = handleCartStamp(coupon);
      var cartId = localStorage.getItem('cartIdd');
      return fetch(HANDLE_CART_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          cartId: cartId ? 'gid://shopify/Cart/' + cartId : '',
          coupon: coupon,
          lineItems: handleCartLineItems(),
          customerEmail: email
        })
      })
        .then(function (r) {
          // An error response (non-2xx) must never be stored as the order's handle-cart; callers
          // treat the rejection like a network failure, as they did an error body before.
          if (!r.ok) throw new Error('handle-cart request failed: ' + r.status);
          return r.json();
        })
        .then(function (result) {
          var gid = result && result.id;
          localStorage.setItem('cartIdd', gid ? gid.split('/Cart/')[1] : '');
          if (stamp === handleCartStamp(coupon)) {
            localStorage.setItem('handle-cart', JSON.stringify(result));
            localStorage.setItem(HANDLE_CART_STAMP, stamp);
          } else if (result && typeof result === 'object') {
            result.stale = true;
          }
          return result;
        });
    });
    handleCartQueued = entry;
    handleCartChain = entry.promise.catch(function () {});
    return entry.promise;
  }

  // For order placement: waits for pending cart writes, the refresh and any handle-cart call in
  // flight, then returns a handle-cart that matches the current lines and coupon, asking the
  // backend again when the stored one doesn't. Rejects (err.userMessage) when it can't get one,
  // so an order is never built from another cart's data.
  var ENSURE_HANDLE_CART_MS = 20000;
  function ensureHandleCart(email) {
    // A hung request (refresh step or handle-cart) would otherwise leave Pay spinning with no word.
    var timer;
    var timeout = new Promise(function (resolve, reject) {
      timer = setTimeout(function () { reject(new Error('timed out after ' + ENSURE_HANDLE_CART_MS + 'ms')); }, ENSURE_HANDLE_CART_MS);
    });
    return Promise.race([whenSettled(), timeout])
      .then(function () { return Promise.race([handleCartChain, timeout]); })
      .then(function () {
        var coupon = (localStorage.getItem('discountCode') || '').trim();
        if (handleCartFresh(coupon)) return readJsonStorage(localStorage, 'handle-cart');
        return Promise.race([requestHandleCart(coupon, email), timeout]).then(function (result) {
          if (!handleCartFresh(coupon)) throw new Error('handle-cart does not match the cart');
          return result;
        });
      })
      .then(function (result) {
        clearTimeout(timer);
        return result;
      }, function (e) {
        clearTimeout(timer);
        console.error('[CartPage] ensureHandleCart', e);
        var err = new Error('handle-cart unavailable');
        err.userMessage = "We couldn't confirm your cart. Please try again.";
        throw err;
      });
  }

  // Rewards lines are capped at one unit.
  function rewardsCapUpdates() {
    var updates = {};
    items().forEach(function (item) {
      if (tagString(item).indexOf('rewards') > -1 && item.quantity > 1) updates[item.variant_id] = 1;
    });
    return updates;
  }

  // Line items for the surprise-gift backend (cart-revamp addSurpriseGiftCart).
  function surpriseGiftItems() {
    return items().map(function (item) {
      return {
        id: item.id,
        price: item.price,
        original_price: item.original_price,
        original_line_price: item.original_line_price,
        line_price: item.line_price,
        final_price: item.final_price,
        final_line_price: item.final_line_price,
        product_type: item.product_type,
        variant_id: String(item.variant_id),
        product_id: String(item.product_id),
        sku: item.sku,
        url: item.url,
        vendor: item.vendor,
        key: item.key,
        title: item.title,
        handle: item.handle,
        product_description: item.product_description,
        image: item.image,
        quantity: item.quantity,
        tags: tagsOf(item)
      };
    });
  }

  function readJsonStorage(storage, key) {
    try {
      var raw = storage.getItem(key);
      return raw && raw !== 'undefined' ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  }

  // Segment `products` for Cart Reviewed / checkout-step events.
  function segmentProducts() {
    var edd = readJsonStorage(sessionStorage, 'cartEdd') || {};
    var coupon = localStorage.getItem('discountCode') || '';
    return items().map(function (item, index) {
      var m = meta(item.key);
      return {
        product_id: String(item.product_id),
        brand: item.vendor,
        coupon: coupon,
        product_name: item.title,
        product_url: item.handle,
        product_value_before_cart_discount: m.product_price / 100,
        product_value_mrp: m.product_price / 100,
        product_value_post_cart_discount: item.final_price / 100,
        quantity: item.quantity,
        sku_id: item.sku,
        sku_mrp: m.product_price / 100,
        sku_selling_price: item.final_price / 100,
        variant_id: String(item.variant_id),
        variant_name: m.variant_title,
        variant_url: m.variant_url,
        category: m.category || {},
        position: index + 1,
        product_coupon_discount: (item.line_level_total_discount || 0) / 100,
        edd: edd[item.sku] || ''
      };
    });
  }

  // GA4 view_cart items.
  function gaItems() {
    var edd = readJsonStorage(sessionStorage, 'cartEdd') || {};
    return items().map(function (item, index) {
      var c = meta(item.key).category || {};
      return {
        item_id: String(item.product_id),
        item_brand: item.vendor,
        coupon: '',
        item_name: item.title,
        item_category: c.l0,
        item_category2: c.l1,
        item_category3: c.l2,
        item_category4: c.l3,
        item_category5: c.l4,
        item_variant: meta(item.key).variant_title,
        quantity: item.quantity,
        price: item.final_price / 100,
        index: index + 1,
        discount: (item.line_level_total_discount || 0) / 100,
        edd: edd[item.sku] || ''
      };
    });
  }

  // sessionStorage.itemInSessionStorage (read by the checkout / payment steps).
  function checkoutItems() {
    var edd = readJsonStorage(sessionStorage, 'cartEdd') || {};
    var eddText = readJsonStorage(sessionStorage, 'cartDeliveryTime') || {};
    var coupon = localStorage.getItem('discountCode');
    return items().map(function (item, index) {
      var m = meta(item.key);
      var c = m.category || {};
      return {
        item_id: String(item.product_id),
        item_sku: item.sku,
        item_brand: item.vendor,
        coupon: coupon,
        item_name: m.product_title,
        item_variant: item.title,
        item_category: c.l0,
        item_category2: c.l1,
        item_category3: c.l2,
        item_category4: c.l3,
        item_category5: c.l4,
        quantity: item.quantity,
        price: item.final_price / 100,
        index: index + 1,
        discount: (item.line_level_total_discount || 0) / 100,
        edd: edd[item.sku] || '',
        eddText: eddText[item.sku] || '',
        mrp: (m.compare_at_price || 0) / 100,
        url: item.url
      };
    });
  }

  /* ---- Formatting ----------------------------------------------------------- */

  function money(cents) {
    if (window.theme && theme.Currency && theme.settings && theme.settings.moneyFormat) {
      return theme.Currency.formatMoney(cents, theme.settings.moneyFormat);
    }
    return '₹' + Math.round(cents / 100).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  // Same markup as snippets/price.liquid.
  function priceHtml(cents) {
    var m = money(cents);
    return '<span aria-hidden="true">' + m + '</span><span class="visually-hidden">' + m + '</span>';
  }

  // Same markup as the `.cart__item-price-col` block in cart-item.liquid.
  function priceColHtml(variantPrice, compareAt, qty) {
    var hasCompare = compareAt > variantPrice && variantPrice !== 0;
    var html = '<span class="product__price-on-sale">' + (variantPrice > 1 ? priceHtml(variantPrice * qty) : '') + '</span>';
    if (hasCompare) {
      html += '<span class="product__price-wrap"><span id="Cart_ComparePrice" class="product__price product__price--compare">' +
        priceHtml(compareAt * qty) + '</span></span>';
    }
    html += '<div class="cart_savings">';
    if (hasCompare) html += '<span class="cart_product__price-savings">' + priceHtml((compareAt - variantPrice) * qty) + ' saved</span>';
    if (variantPrice <= 1) html += '<span class="cart_product__free">FREE</span>';
    html += '</div>';
    return html;
  }

  // The line a BXGY coupon put in the cart for free (bxgyData is written by cart-revamp's
  // callApplyCoupon: bxgyItem = variant, bxgyAdded = free units). It shows no quantity buttons and
  // its price struck through with FREE. Only when every unit on the line is a free one: a line the
  // shopper also bought units on stays a normal, editable line.
  function bxgyFreeUnits() {
    var d = readJsonStorage(localStorage, 'bxgyData');
    if (!d || !d.bxgyItem) return null;
    var qty = d.bxgyAdded !== undefined ? Number(d.bxgyAdded) || 0 : Number(d.bxgyCount) || 1;
    return qty > 0 ? { variant: String(d.bxgyItem), qty: qty } : null;
  }

  function isBxgyFreeLine(variantId, properties, qty, free) {
    return !!free && String(variantId) === free.variant && qty > 0 && qty <= free.qty &&
      !(properties && Object.keys(properties).length);
  }

  function bxgyFreePriceColHtml(variantPrice, qty) {
    return '<span class="product__price-on-sale"></span>' +
      '<span class="product__price-wrap"><span id="Cart_ComparePrice" class="product__price product__price--compare">' +
      priceHtml(variantPrice * qty) + '</span></span>' +
      '<div class="cart_savings"><span class="cart_product__free">FREE</span></div>';
  }

  /* ---- DOM ------------------------------------------------------------------ */

  var STYLE = [
    '.cart-page-busy #progBarPayment,.cart-page-busy #progBarAddress,.cart-page-busy #progBarReview,',
    '.cart-page-busy #sum_cart__checkout_submit,.cart-page-busy #cart__checkout_submit,',
    '.cart-page-busy #cart__checkout_submit-desk,.cart-page-busy #add_address_desk{pointer-events:none!important;opacity:.6}',
    '.cart-page-pending{animation:cartPagePulse 1s ease-in-out infinite}',
    '@keyframes cartPagePulse{0%,100%{opacity:1}50%{opacity:.35}}',
    '.cart-page-row-error{color:#d32f2f;font-size:12px;line-height:16px;margin-top:4px}',
    '.cart__item.cart-page-removing{opacity:.4;pointer-events:none;transition:opacity .2s}',
    '[data-cart-page-hidden]{display:none!important}',
    '.cart__item.cart-page-bxgy-free .qty-container{display:none!important}'
  ].join('');

  function injectStyle() {
    var s = document.createElement('style');
    s.setAttribute('data-cart-page', '');
    s.textContent = STYLE;
    document.head.appendChild(s);
  }

  function form() { return document.getElementById('cartpage_form'); }

  function allRows() {
    var f = form();
    return f ? Array.prototype.slice.call(f.querySelectorAll('.cart__item[data-key]')) : [];
  }

  function rowEl(key) {
    var rows = allRows();
    for (var i = 0; i < rows.length; i++) if (rows[i].getAttribute('data-key') === key) return rows[i];
    return null;
  }

  // The price-block nodes are static (only their contents change), and scanning the ~5k-node
  // page for them on every tap was measurable. Look them up once; re-query only if one left the DOM.
  var nodeCache = {};
  function cachedNodes(selector) {
    var nodes = nodeCache[selector];
    if (!nodes || nodes.some(function (el) { return !el.isConnected; })) {
      nodes = Array.prototype.slice.call(document.querySelectorAll(selector));
      nodeCache[selector] = nodes;
    }
    return nodes;
  }

  function eachId(id, fn) {
    cachedNodes('[id="' + id + '"]').forEach(fn);
  }

  function lastSpan(el) { return el.querySelector(':scope > span:last-child'); }

  // Row DOM for a given quantity — used optimistically on tap and again from server state.
  function paintRow(row, item, m, qty) {
    var q = String(qty);
    row.setAttribute('data-product-quantity', q);
    var wrapper = row.querySelector('.js-qty__wrapper');
    if (wrapper) wrapper.setAttribute('data-product-quantity', q);
    var input = row.querySelector('.js-qty__num');
    if (input) input.value = q;
    var plus = row.querySelector('.js-qty__adjust--plus');
    if (plus) {
      var maxed = m.inventory_quantity === qty;
      plus.disabled = maxed;
      plus.classList.toggle('disabled', maxed);
    }
    var link = row.querySelector('[id="revamp_cartremove"]');
    if (link) link.setAttribute('data-product-quantity', q);
    var edd = row.querySelector('.cart_edd');
    if (edd) edd.setAttribute('data-prodquantity', q);
    var free = isBxgyFreeLine(row.getAttribute('data-variant-id'), item && item.properties, qty, bxgyFreeUnits());
    row.classList.toggle('cart-page-bxgy-free', free);
    var col = row.querySelector('.cart__item-price-col');
    if (col) {
      col.innerHTML = free
        ? bxgyFreePriceColHtml(m.variant_price || (item && item.price) || 0, qty)
        : priceColHtml(m.variant_price || 0, m.compare_at_price || 0, qty);
    }
    if (item) {
      row.setAttribute('data-product-price', item.price);
      if (link) {
        link.setAttribute('data-product-price', item.final_price);
        link.setAttribute('data-product-compare-price', item.original_price);
      }
    }
  }

  function removeRowEl(row) {
    var prev = row.previousElementSibling;
    if (prev && prev.classList.contains('reviewCartText')) prev.remove();
    row.remove();
  }

  // Order-summary numbers from a totals object (server state, or the optimistic estimate).
  function paintTotals(t, itemCount) {
    eachId('cart_mrp', function (el) {
      var s = lastSpan(el);
      if (s) s.innerHTML = priceHtml(t.mrp);
    });
    eachId('cart_discounton_mrp', function (el) {
      var s = lastSpan(el);
      if (s) s.innerHTML = '- &nbsp;' + priceHtml(t.savings);
      el.style.display = t.savings > 0 ? '' : 'none';
    });
    eachId('item_count', function (el) {
      el.textContent = itemCount + ' ' + (itemCount === 1 ? 'Item' : 'Items');
    });
    cachedNodes('.cart-link__bubble-num').forEach(function (el) { el.textContent = itemCount; });
  }

  function paintServerTotals() {
    var c = cart();
    var t = totals();
    paintTotals(t, c.item_count);
    ['desktop_final_price', 'desktop_final_price_summary'].forEach(function (id) {
      var el = document.getElementById(id);
      if (el) el.setAttribute('data-carttotalamount', c.original_total_price);
    });
    // Provisional totals until fees / coupon land (they overwrite these with the payable amount).
    finalPriceNodes().forEach(function (el) { el.innerHTML = priceHtml(t.final); });
    var f = form();
    if (f) {
      f.setAttribute('data-count', c.item_count);
      f.setAttribute('data-cart-subtotal', c.total_price);
      f.setAttribute('data-cartsavingonmrp', t.savings);
    }
    // With a coupon applied, applyDiscount counts this up to MRP savings + coupon instead.
    if (!localStorage.getItem('discountCode')) callGlobal('changeSavings', shownSavings(), Math.round(t.savings / 100));
  }

  // Optimistic order summary: MRP, savings and item count are derivable from the lines;
  // everything payable (fees, coupon) is left to the refresh and shown as pending.
  function paintOptimisticTotals() {
    var t = { mrp: 0, savings: 0 };
    var count = 0;
    items().forEach(function (item) {
      var qty = item.key in desired ? desired[item.key] : item.quantity;
      if (removing[item.key]) qty = 0;
      var m = meta(item.key);
      var compare = m.compare_at_price || 0;
      var price = m.variant_price || 0;
      t.mrp += compare * qty;
      if (compare > price) t.savings += (compare - price) * qty;
      count += qty;
    });
    paintTotals(t, count);
    setPending(true);
  }

  function finalPriceNodes() {
    return cachedNodes('.final_price > span:last-child, .final_price_summary > span:last-child, #mobile_total_price, #mobile_total_price_summ');
  }

  function setPending(on) {
    finalPriceNodes().forEach(function (el) { el.classList.toggle('cart-page-pending', on); });
  }

  // Checkout / payment controls stay locked from the first tap until the refresh has finished:
  // through the debounce, the write and the refresh that recomputes totals and handle-cart.
  function updateBusy() {
    var busy = refreshing || !idle();
    document.body.classList.toggle('cart-page-busy', busy);
    if (!busy) setPending(false);
  }

  function showRowError(row, message) {
    if (!row) return;
    var box = row.querySelector('.cart-page-row-error');
    if (!box) {
      box = document.createElement('div');
      box.className = 'cart-page-row-error';
      box.setAttribute('role', 'alert');
      var anchor = row.querySelector('.cart__item-details') || row;
      anchor.appendChild(box);
    }
    box.textContent = message;
    clearTimeout(box._t);
    box._t = setTimeout(function () { box.remove(); }, ROW_ERROR_MS);
  }

  /* ---- Empty <-> non-empty ------------------------------------------------------ */

  var itemsCallbacks = [];  // run once, the first time the cart has items
  var emptyCallbacks = [];  // run every time the cart is (or becomes) empty
  var wasEmpty = !state.cart.item_count;

  function onItems(fn) {
    if (cart().item_count) fn();
    else itemsCallbacks.push(fn);
  }

  function onEmpty(fn) {
    emptyCallbacks.push(fn);
    if (!cart().item_count) fn();
  }

  function runCallbacks(list) {
    list.forEach(function (fn) {
      try { fn(); } catch (e) { console.error('[CartPage] callback', e); }
    });
  }

  function setHidden(el, hidden) {
    if (!el) return;
    if (hidden) el.setAttribute('data-cart-page-hidden', '');
    else el.removeAttribute('data-cart-page-hidden');
  }

  // Same structure the server renders for the current cart.
  function showCartView(empty) {
    setHidden(document.getElementById('CartPageForm'), empty);
    Array.prototype.forEach.call(document.querySelectorAll('[data-cart-page-empty]'), function (el) {
      setHidden(el, !empty);
    });
    var header = document.querySelector('[data-cart-page-header]');
    if (header) header.classList.toggle('section-header--404', empty);
    var root = document.querySelector('[data-cart-page-root]');
    if (root) root.classList.toggle('page-width--cart', !empty);
  }

  function closeCartPopups() {
    ['cart_remove_itempopup', 'cartcouponpopup'].forEach(function (id) {
      var el = document.getElementById(id);
      if (el) el.classList.remove('open-delete-popup', 'open-coupon-popup');
    });
    ['cart_remove_itemcontainer', 'cartcouponcontainer'].forEach(function (id) {
      var el = document.getElementById(id);
      if (el) el.classList.remove('active');
    });
    document.body.style.overflow = '';
  }

  /* ---- Network -------------------------------------------------------------- */

  function routes() { return (window.theme && theme.routes) || {}; }

  function rejected(message) {
    var e = new Error(message || 'Could not update your cart');
    e.rejected = true;
    return e;
  }

  function postCart(url, body) {
    body.sections = STATE_SECTION;
    body.sections_url = '/cart';
    return fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    })
      .then(function (r) {
        return r.text().then(function (text) {
          logTiming('write ' + url, { bytes: text.length, status: r.status });
          var data = null;
          try { data = JSON.parse(text); } catch (e) {}
          return { ok: r.ok, status: r.status, data: data };
        });
      })
      .then(function (res) {
        // A server error page (5xx / 429 HTML) is not JSON: say the cart couldn't be updated, not
        // that the shopper's connection failed.
        if (!res.data) {
          throw rejected(res.status === 429 ? 'Too many requests. Please wait a moment and try again.' : "Couldn't update your cart. Please try again.");
        }
        if (!res.ok || res.data.status) throw rejected(res.data.description || res.data.message);
        var next = stateFromHtml(res.data.sections && res.data.sections[STATE_SECTION]);
        return next || fetchState();
      });
  }

  function postChange(key, qty) {
    return postCart(routes().cartChange || '/cart/change.js', { id: key, quantity: qty });
  }

  function postUpdate(updates) {
    return postCart('/cart/update.js', { updates: updates });
  }

  function fetchState() {
    return fetch('/cart?sections=' + STATE_SECTION, { credentials: 'same-origin' })
      .then(function (r) {
        if (!r.ok) throw new Error('cart state request failed: ' + r.status);
        return r.json();
      })
      .then(function (data) { return stateFromHtml(data[STATE_SECTION]); });
  }

  function fetchRows() {
    return fetch('/cart?sections=' + ROWS_SECTION, { credentials: 'same-origin' })
      .then(function (r) {
        if (!r.ok) throw new Error('cart rows request failed: ' + r.status);
        return r.json();
      })
      .then(function (data) {
        var tpl = document.createElement('template');
        tpl.innerHTML = data[ROWS_SECTION] || '';
        return tpl.content;
      });
  }

  /* ---- Reconcile rows with state -------------------------------------------- */

  // Every row from the current state (rows the shopper is still editing keep their qty). Used once
  // the page loads and whenever BXGY data may have changed without a cart write.
  function paintAllRows() {
    allRows().forEach(function (row) {
      var key = row.getAttribute('data-key');
      var item = findItem(key);
      if (item && !(key in desired)) paintRow(row, item, meta(key), item.quantity);
    });
  }

  function renderRowsFromState() {
    var byKey = {};
    items().forEach(function (item) { byKey[item.key] = item; });
    allRows().forEach(function (row) {
      var key = row.getAttribute('data-key');
      var item = byKey[key];
      if (!item) {
        if (!removing[key]) removeRowEl(row);
        return;
      }
      if (!(key in desired)) paintRow(row, item, meta(key), item.quantity);
    });
    var missing = items().filter(function (item) { return !rowEl(item.key); });
    return missing.length ? insertRows(missing) : Promise.resolve();
  }

  // A line the page never rendered (gift, BXGY item, …): pull its row from the rows section
  // and place it next to its neighbour in cart order.
  function insertRows(missing) {
    return fetchRows().then(function (frag) {
      var container = document.getElementById('cartRender') || form();
      if (!container) return;
      var order = items().map(function (item) { return item.key; });
      missing.forEach(function (item) {
        var fresh = frag.querySelector('.cart__item[data-key="' + item.key.replace(/"/g, '\\"') + '"]');
        if (!fresh || rowEl(item.key)) return;
        // cart-item.liquid renders a delivery-count banner before each row; bring it along when
        // the page has none (it loaded with an empty cart), since EDD writes into it.
        var banner = fresh.previousElementSibling;
        var addBanner = banner && banner.classList.contains('reviewCartText') && !document.getElementById('reviewCartText');
        var next = null;
        for (var i = order.indexOf(item.key) + 1; i < order.length && !next; i++) next = rowEl(order[i]);
        var anchor = next && next.parentNode === container ? next : null;
        if (addBanner) container.insertBefore(banner, anchor);
        container.insertBefore(fresh, anchor);
      });
      // Inserted rows are the server's markup; give them the same treatment as patched rows.
      missing.forEach(function (item) {
        var r = rowEl(item.key);
        if (r && !(item.key in desired)) paintRow(r, item, meta(item.key), item.quantity);
      });
      callGlobal('moveSurpriseDiv');
      callGlobal('decorateBxgyItem');
    }).catch(function (e) {
      console.error('[CartPage] could not render new cart rows', e);
    });
  }

  /* ---- Mutation queue -------------------------------------------------------- */

  var desired = {};   // key -> quantity the shopper asked for, not yet confirmed
  var timers = {};    // key -> debounce timer
  var removing = {};  // key -> true while a removal is in flight
  var tapEvents = {}; // key -> analytics for +/- taps, sent once the server confirms the quantity
  var chain = Promise.resolve();
  var inflight = 0;

  // Run `task` in order with the other cart writes, without counting as a shopper change
  // (no busy lock / refresh of its own). Resolves with the task's result.
  function inQueue(task) {
    var p = chain.then(task);
    chain = p.catch(function () {});
    return p;
  }

  function enqueue(task) {
    inflight++;
    updateBusy();
    chain = chain.then(task).catch(function (e) {
      console.error('[CartPage] mutation', e);
    }).then(function () {
      inflight--;
      maybeRefresh();
      updateBusy();
    });
  }

  function currentQty(key) {
    if (key in desired) return desired[key];
    var item = findItem(key);
    return item ? item.quantity : 0;
  }

  function setDesired(key, qty) {
    var row = rowEl(key);
    desired[key] = qty;
    if (row) paintRow(row, findItem(key), meta(key), qty);
    paintOptimisticTotals();
    logTiming('optimistic paint');
    clearTimeout(timers[key]);
    timers[key] = setTimeout(function () {
      delete timers[key];
      enqueue(function () { return sendQty(key); });
    }, QTY_DEBOUNCE_MS);
    updateBusy();
  }

  function sendQty(key) {
    if (!(key in desired)) return null;
    var qty = desired[key];
    // The taps this request carries; taps made while it's in flight go with the next one.
    var taps = tapEvents[key] || [];
    delete tapEvents[key];
    return postChange(key, qty).then(function (next) {
      if (desired[key] === qty) delete desired[key];
      setState(next);
      var line = findItem(key);
      if (line && line.quantity === qty) {
        taps.forEach(function (send) { send(); });
      } else {
        // The server settled on another quantity (e.g. capped at stock): the optimistic totals are
        // wrong even if the cart matches what it was, so refresh; the taps weren't all applied.
        mutated = true;
      }
      return renderRowsFromState();
    }, function (err) {
      if (desired[key] === qty) delete desired[key];
      return recover(key, err);
    });
  }

  // Put the row back to the server's truth and say why, without touching the rest of the cart.
  function recover(key, err) {
    var message = err && err.rejected
      ? err.message
      : "Couldn't update your cart. Please check your connection and try again.";
    return fetchState().then(setState, function () {}).then(function () {
      return renderRowsFromState();
    }).then(function () {
      var row = rowEl(key);
      if (row) row.classList.remove('cart-page-removing');
      showRowError(row, message);
      paintServerTotals();
      // Other rows may still have taps waiting to be sent: keep their quantities in the totals.
      if (Object.keys(desired).length || Object.keys(removing).length) paintOptimisticTotals();
      // paintServerTotals leaves the provisional total (no fees / coupon) on screen; the refresh
      // puts the payable amount back even though the cart itself didn't change.
      mutated = true;
    });
  }

  function removeLine(key) {
    if (!key) return;
    var row = rowEl(key);
    var snapshot = row ? analyticsSnapshot(row) : null;
    clearTimeout(timers[key]);
    delete timers[key];
    delete desired[key];
    delete tapEvents[key];  // never sent; the removal is reported instead
    removing[key] = true;
    if (row) row.classList.add('cart-page-removing');
    paintOptimisticTotals();
    enqueue(function () {
      return postChange(key, 0).then(function (next) {
        delete removing[key];
        trackRemoved(snapshot);
        var bxgy = readJsonStorage(localStorage, 'bxgyData');
        if (bxgy && String(bxgy.bxgyItem) === key.split(':')[0]) localStorage.removeItem('bxgyData');
        setState(next);
        return renderRowsFromState();
      }, function (err) {
        delete removing[key];
        return recover(key, err);
      });
    });
  }

  // Queue a cart write for other cart code; resolves once the new state is on the page.
  // Always settles: a throw while applying the new state rejects instead of leaving callers
  // (and a batch's hold) waiting forever.
  function mutate(send) {
    return new Promise(function (resolve, reject) {
      enqueue(function () {
        return send().then(function (next) {
          setState(next);
          return renderRowsFromState();
        }, function (err) {
          var fail = function () { throw err; };
          return fetchState().then(setState, function () {}).then(renderRowsFromState).then(fail, fail);
        }).then(function () { resolve(state.cart); }, reject);
      });
    });
  }

  // `id` is a line key or a variant id (Shopify accepts either).
  function change(id, qty) {
    return mutate(function () { return postChange(String(id), qty); });
  }

  function add(items) {
    return mutate(function () { return postCart('/cart/add.js', { items: items }); });
  }

  function update(updates) {
    return mutate(function () { return postUpdate(updates); });
  }

  // Hold the refresh while `fn` runs several writes (e.g. swap a BXGY item), then refresh once.
  // `opts.coupon` ({ code, action, showSuccessPopup }) makes that refresh apply this coupon as a
  // shopper action instead of silently re-applying the stored one.
  var holds = 0;
  function batch(fn, opts) {
    holds++;
    updateBusy();
    var done = function () {
      holds--;
      mutated = true;
      if (opts) queuedOpts = opts;
      maybeRefresh();
      updateBusy();
    };
    return Promise.resolve().then(fn).then(function (r) { done(); return r; }, function (e) { done(); throw e; });
  }

  // Apply the rewards cap (rewards lines are limited to one unit) and return whether it changed anything.
  // Queued behind any write already in flight, so its response can't replace a newer cart.
  function enforceRewardsCap() {
    return inQueue(function () {
      var updates = rewardsCapUpdates();
      if (!Object.keys(updates).length) return false;
      return postUpdate(updates).then(function (next) {
        setState(next);
        return renderRowsFromState().then(function () { return true; });
      });
    }).catch(function (e) {
      console.error('[CartPage] rewards cap', e);
      return false;
    });
  }

  /* ---- Refresh pipeline ------------------------------------------------------ */

  var refreshing = false;
  var refreshAgain = false;
  var queuedOpts = null;
  var settleWaiters = [];

  function idle() {
    return inflight === 0 && holds === 0 && !Object.keys(timers).length;
  }

  function maybeRefresh() {
    if (idle() && mutated) refresh(queuedOpts);
    else if (idle()) flushSettled();
  }

  function flushSettled() {
    if (!idle() || refreshing) return;
    var waiters = settleWaiters;
    settleWaiters = [];
    waiters.forEach(function (fn) { fn(cart()); });
  }

  // Resolves once no cart write is pending and the refresh has finished.
  function whenSettled() {
    return new Promise(function (resolve) {
      settleWaiters.push(resolve);
      flushSettled();
    });
  }

  function callGlobal(name) {
    var fn = window[name];
    if (typeof fn !== 'function') return Promise.resolve();
    var args = Array.prototype.slice.call(arguments, 1);
    try {
      return Promise.resolve(fn.apply(window, args)).catch(function (e) {
        console.error('[CartPage] ' + name, e);
      });
    } catch (e) {
      console.error('[CartPage] ' + name, e);
      return Promise.resolve();
    }
  }

  function shownSavings() {
    var el = document.getElementById('savings_counter_desk');
    return el ? parseInt(el.innerText, 10) || 0 : 0;
  }

  function surpriseGiftEnabled() {
    /* eslint-disable-next-line no-undef */
    return typeof surpriseGiftCart !== 'undefined' && surpriseGiftCart === true;
  }

  // Everything a reload used to recompute, in dependency order. Fees before the coupon so the
  // coupon's discounted total is the one left on screen.
  //
  // Rule for the steps below: a step must never wait on CartPage.refresh() or whenSettled() —
  // both resolve only after this pass ends, so the pass would wait on itself forever. Steps may
  // write the cart (CartPage.update / change / add); those run in the queue, and the change
  // triggers one more pass afterwards. REFRESH_STUCK_MS logs a pass that never finishes.
  var REFRESH_STUCK_MS = 20000;
  function refresh(opts) {
    if (refreshing) {
      refreshAgain = true;
      if (opts) queuedOpts = opts;
      return;
    }
    queuedOpts = null;
    mutated = false;
    if (!cart().item_count) {
      // Emptied: switch to the empty state; there is nothing for fees / coupons / EDD to do.
      closeCartPopups();
      showCartView(true);
      paintServerTotals();
      updateBusy();
      if (!wasEmpty) {
        wasEmpty = true;
        window.scrollTo({ top: 0, behavior: 'smooth' });
        runCallbacks(emptyCallbacks);
      }
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: cart() } }));
      flushSettled();
      return;
    }
    var gainedItems = wasEmpty;
    if (gainedItems) {
      wasEmpty = false;
      showCartView(false);
    }
    refreshing = true;
    updateBusy();
    paintServerTotals();
    setPending(true);
    var stuck = setTimeout(function () {
      console.error('[CartPage] refresh still running after ' + REFRESH_STUCK_MS + 'ms — a step may be waiting on CartPage.refresh() / whenSettled()');
    }, REFRESH_STUCK_MS);
    enforceRewardsCap()
      .then(function () { return callGlobal('getVariousFees', cart()); })
      .then(function () {
        var coupon = opts && opts.coupon;
        if (coupon && coupon.code) {
          return callGlobal('applyDiscount', coupon.code, coupon.action || 't', true, !!coupon.showSuccessPopup);
        }
        var code = localStorage.getItem('discountCode');
        // Same re-apply the reload used to do; flagged so it isn't reported as a new application.
        if (code) return callGlobal('applyDiscount', code, 't', true, false, { reapply: true });
        callGlobal('clearAppliedCouponUI');
        // Skip the call when the stored handle-cart is already the coupon-less one for these lines
        // (coupon removal just stored it).
        var handleCartDone = handleCartFresh('');
        // Independent calls: order data (handle-cart, can take seconds) and the best-coupon chip.
        return Promise.all([handleCartDone ? null : callGlobal('refreshHandleCart'), callGlobal('refreshCoupons')]);
      })
      .then(function () {
        // Gift changes land before EDD / the review step, so those run once on the final cart.
        // Surprise gift is re-checked after every change, pincode or not (the reload used to do it:
        // with a pincode through #changeeddbtn, without one through appendPharmaPage). Its check is
        // keyed on cart + location, so an unchanged cart costs nothing.
        var gifts = [callGlobal('CommpleteFreeGiftLoadNew', cart())];
        if (surpriseGiftEnabled()) gifts.push(callGlobal('addSurpriseGiftCart', cart()));
        return Promise.all(gifts);
      })
      .then(function () { return callGlobal('cartpageedd', { force: true }); })
      .then(function () { return callGlobal('summaryPageCall'); })
      .catch(function (e) { console.error('[CartPage] refresh', e); })
      .then(function () {
        clearTimeout(stuck);
        paintAllRows();
        if (gainedItems) {
          var pending = itemsCallbacks;
          itemsCallbacks = [];
          runCallbacks(pending);
        }
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: cart() } }));
        logTiming('settled');
        refreshing = false;
        updateBusy();
        if (refreshAgain) {
          // Run the follow-up pass once no write is pending (maybeRefresh), not on top of one.
          refreshAgain = false;
          mutated = true;
          maybeRefresh();
        } else {
          flushSettled();
        }
      });
  }

  /* ---- Analytics (one event per shopper action) ------------------------------ */

  function track(event, payload) {
    try {
      if (window.analytics && typeof window.analytics.track === 'function') window.analytics.track(event, payload);
    } catch (e) {
      console.error('[CartPage] ' + event, e);
    }
  }

  function commonContext() {
    var context = {};
    var location = null;
    try { context = window.sessionCore ? window.sessionCore.getMergedContext() : {}; } catch (e) {}
    try { location = typeof window.getLocationData === 'function' ? window.getLocationData() : null; } catch (e) {}
    var out = {
      cart_id: localStorage.getItem('cartId') || null,
      cart_key: localStorage.getItem('cartKey') || null,
      platform: 'web',
      user_id: localStorage.getItem('cid') || null
    };
    if (location) out.user_location = location;
    for (var k in context) out[k] = context[k];
    return out;
  }

  function eddFor(sku) {
    return typeof window.productEddData === 'function' ? window.productEddData(sku) : null;
  }

  // Per-tap +/- events (same payload the theme's QtySelector sent).
  function trackQtyTap(row, added) {
    var wrapper = row.querySelector('.js-qty__wrapper');
    var eddEl = row.querySelector('.cart_edd');
    var titleEl = row.querySelector('.cart__item-name');
    if (!wrapper) return;
    var sku = eddEl ? eddEl.getAttribute('data-prodsku') : '';
    var eddData = eddFor(sku);
    var price = wrapper.getAttribute('data-product-price');
    var product = {
      brand: eddEl ? eddEl.getAttribute('data-prodvendor') : '',
      category: {
        l0: wrapper.getAttribute('data-category-l0') || '',
        l1: wrapper.getAttribute('data-category-l1') || '',
        l2: wrapper.getAttribute('data-category-l2') || '',
        l3: wrapper.getAttribute('data-category-l3') || '',
        l4: wrapper.getAttribute('data-category-l4') || ''
      },
      edd: (eddEl && eddEl.innerText) || (eddData && eddData.delivery_timeslot_label) || '',
      position: 0,
      product_id: parseInt(row.getAttribute('data-product-id'), 10) || '',
      product_name: titleEl ? titleEl.textContent.trim() : '',
      product_url: titleEl ? titleEl.getAttribute('href') || '' : '',
      sku_id: sku || (added ? null : ''),
      sku_mrp: parseInt(wrapper.getAttribute('data-product-compare-price'), 10) / 100 || '',
      sku_selling_price: parseInt(price, 10) / 100 || '',
      variant_id: parseInt(row.getAttribute('data-variant-id'), 10) || '',
      variant_name: '',
      variant_url: titleEl ? titleEl.getAttribute('href') || '' : '',
      quantity: 1
    };
    if (eddData) product.edd_response = eddData;
    var payload = commonContext();
    payload.product = product;
    payload[added ? 'add_to_cart_source' : 'remove_from_cart_source'] = 'cart';
    track(added ? 'Product Added' : 'Product Removed', payload);

    var variantId = row.getAttribute('data-variant-id');
    var productId = row.getAttribute('data-product-id');
    var rupees = price ? parseInt(price, 10) / 100 : null;
    if (added && typeof window.trackUnbxdAddToCart === 'function') {
      window.trackUnbxdAddToCart({ product_id: productId, variant_id: variantId }, 1, rupees);
    } else if (!added && typeof window.trackUnbxdCartRemove === 'function') {
      window.trackUnbxdCartRemove(variantId, productId, 1, rupees);
    }
  }

  // Captured before the row goes away.
  function analyticsSnapshot(row) {
    var link = row.querySelector('[id="revamp_cartremove"]');
    return link ? Object.assign({}, link.dataset) : null;
  }

  // Remove-confirm event (fired on confirm; it used to fire when the popup opened).
  function trackRemoved(d) {
    if (!d) return;
    var productId = parseInt(d.productId || '', 10);
    var variantId = parseInt(d.variantId || '', 10);
    var quantity = parseInt(d.productQuantity || '1', 10) || 1;
    var eddData = eddFor(d.productSku);
    var product = {
      brand: d.productVendor || null,
      category: {
        l0: d.categoryL0 || null,
        l1: d.categoryL1 || null,
        l2: d.categoryL2 || null,
        l3: d.categoryL3 || null,
        l4: d.categoryL4 || null
      },
      position: parseInt(d.productPosition || '0', 10) || 0,
      product_id: isNaN(productId) ? null : productId,
      variant_id: isNaN(variantId) ? null : variantId,
      product_name: d.productTitle || null,
      product_url: d.productUrl || null,
      sku_id: d.productSku || null,
      sku_selling_price: (parseInt(d.productPrice || '0', 10) || 0) / 100 || 0,
      sku_mrp: (parseInt(d.productComparePrice || '0', 10) || 0) / 100 || 0,
      quantity: quantity,
      edd: d.edd || (eddData && eddData.delivery_timeslot_label) || null
    };
    if (eddData) product.edd_response = eddData;
    var payload = commonContext();
    payload.remove_from_cart_source = 'cart';
    payload.product = product;
    track('Product Removed', payload);
    if (typeof window.trackUnbxdCartRemove === 'function') {
      var price = d.productPrice ? parseInt(d.productPrice, 10) / 100 : null;
      window.trackUnbxdCartRemove(d.variantId, d.productId, quantity, price);
    }
  }

  /* ---- Interaction ------------------------------------------------------------ */

  // Product Added / Removed (Segment, Unbxd) for a +/- tap is held until the server accepts the new
  // quantity, so a tap that fails (e.g. out of stock) is never reported.
  function queueTapEvent(key, row, added) {
    (tapEvents[key] = tapEvents[key] || []).push(function () { trackQtyTap(row, added); });
  }

  function openRemovePopup(row) {
    var link = row.querySelector('[id="revamp_cartremove"]');
    var popup = document.getElementById('cart_remove_itempopup');
    var wrap = document.getElementById('cart_remove_itemcontainer');
    var confirm = document.getElementById('cartitemremoveclose');
    if (!link || !popup || !wrap || !confirm) return;
    var img = document.getElementById('itemremove_img');
    if (img) {
      img.setAttribute('alt', link.getAttribute('data-removeproductalt') || '');
      img.setAttribute('srcset', link.getAttribute('data-removeproductimg') || '');
    }
    popup.classList.add('open-delete-popup');
    wrap.classList.add('active');
    document.body.style.overflow = 'hidden';
    confirm.setAttribute('data-removeproduct', row.getAttribute('data-key'));
  }

  function onClick(e) {
    var target = e.target;
    if (!target.closest) return;
    var row = target.closest('.cart__item[data-key]');
    if (!row || !form() || !form().contains(row)) return;
    markTap();
    var key = row.getAttribute('data-key');

    if (target.closest('[id="revamp_cartremove"]')) {
      e.preventDefault();
      openRemovePopup(row);
      return;
    }
    var plus = target.closest('.js-qty__adjust--plus');
    var minus = target.closest('.js-qty__adjust--minus, .cart-page-qty-minus-btn');
    if ((!plus && !minus) || removing[key] || row.classList.contains('cart-page-bxgy-free')) return;
    if ((plus || minus).disabled) return;
    e.preventDefault();

    var qty = currentQty(key);
    if (minus) {
      if (qty <= 1) {
        openRemovePopup(row);
        return;
      }
      setDesired(key, qty - 1);
      queueTapEvent(key, row, false);
      return;
    }
    var inventory = meta(key).inventory_quantity;
    if (inventory > 0 && qty + 1 > inventory) {
      showRowError(row, 'Only ' + inventory + ' left in stock');
      return;
    }
    setDesired(key, qty + 1);
    queueTapEvent(key, row, true);
  }

  function onChange(e) {
    var input = e.target;
    if (!input.classList || !input.classList.contains('js-qty__num')) return;
    var row = input.closest('.cart__item[data-key]');
    if (!row || removing[row.getAttribute('data-key')]) return;
    var key = row.getAttribute('data-key');
    if (row.classList.contains('cart-page-bxgy-free')) { input.value = currentQty(key); return; }
    var qty = parseInt(input.value, 10);
    var current = currentQty(key);
    if (isNaN(qty)) {
      input.value = current;
      return;
    }
    if (qty < 1) {
      input.value = current;
      openRemovePopup(row);
      return;
    }
    var inventory = meta(key).inventory_quantity;
    if (inventory > 0 && qty > inventory) qty = inventory;
    if (qty !== current) setDesired(key, qty);
    else input.value = current;
  }

  function init() {
    injectStyle();
    installAddressReadCache();
    document.addEventListener('click', onClick);
    document.addEventListener('change', onChange);
    // theme.liquid refreshes window.shopifyCart from /cart.js once on load; if the shopper
    // already changed the cart, keep ours (it is newer).
    window.addEventListener('shopifyCartReady', function () {
      if (changedSinceLoad) window.shopifyCart = state.cart;
    });
    document.addEventListener('DOMContentLoaded', function () {
      if (!initialState) {
        // The page's state is missing or broken: load it rather than treat the cart as empty. The
        // refresh that follows shows the cart view and runs the onItems work if it has items.
        console.error('[CartPage] no cart state on the page; loading it from the server');
        fetchState().then(function (next) {
          if (!next) throw new Error('empty state response');
          setState(next);
          mutated = true;
          maybeRefresh();
        }).catch(function (e) { console.error('[CartPage] could not load cart state', e); });
        return;
      }
      paintAllRows();
      enforceRewardsCap().then(function (changed) {
        if (changed) { mutated = true; maybeRefresh(); }
      });
    });
  }

  window.shopifyCart = window.shopifyCart || state.cart;

  window.CartPage = {
    cart: cart,
    items: items,
    totals: totals,
    line: meta,
    tagsById: tagsById,
    tagsList: tagsList,
    collectionHandles: collectionHandles,
    allCollectionHandles: allCollectionHandles,
    tagsToIdFinal: tagsToIdFinal,
    surpriseGiftVariantIds: surpriseGiftVariantIds,
    surpriseGiftItems: surpriseGiftItems,
    medicineItems: medicineItems,
    handleCartLineItems: handleCartLineItems,
    handleCartKey: handleCartKey,
    requestHandleCart: requestHandleCart,
    ensureHandleCart: ensureHandleCart,
    segmentProducts: segmentProducts,
    gaItems: gaItems,
    checkoutItems: checkoutItems,
    removeLine: removeLine,
    change: change,
    add: add,
    update: update,
    batch: batch,
    whenSettled: whenSettled,
    onItems: onItems,
    onEmpty: onEmpty,
    dedupe: dedupe,
    forget: forget,
    refresh: function (opts) {
      mutated = true;
      if (idle()) refresh(opts);
      else if (opts) queuedOpts = opts;
      return whenSettled();
    }
  };

  init();
})();
